/**
 * @module stores/assistant
 * @description 운영 어시스턴트(플로팅 챗봇) 전역 상태 Pinia 스토어.
 * 기존 단일 위젯(AssistantWidget.vue)의 로컬 data를 스토어로 분리하여
 * FAB·패널·composable이 공유한다. activePartnerId는 ADMIN의 접수 commit 타겟
 * (PartnerSelect Vuex→Pinia bridge로 동기화 — AA-44).
 */
import { defineStore } from 'pinia'
import store from '@/store'
import { clearConversation, saveConversation, sanitizeTask, taskOfBlock } from '@/stores/assistantStorage'

// 메시지 안정 식별자 시퀀스 — v-for :key 안정화(중간 메시지 splice 시 재렌더 최소화)
let uidSeq = 0

// 표 직렬화에 싣는 행 ID 수(AA-146)
const TABLE_ID_ROWS = 5

/**
 * 표 상위 행의 식별자 — "첫 번째 건 처리 이력" 류 후속 발화가 행을 가리킬 수 있게 한다(AA-146).
 * rowNav id 만 싣는다: 서버가 도구 결과 ID 로 검증한 값이라(환각 ID 는 null) 셀 원문(고객명·전화 등)을
 * 이력에 새로 싣지 않는다. rowNav 가 없거나 전부 null 이면 빈 문자열.
 * @param {object} d - table 블록 data
 * @returns {string} ' · 행 ID: 1) …, 2) -, …' 또는 ''
 */
function tableRowIds(d) {
  const nav = Array.isArray(d.rowNav) ? d.rowNav.slice(0, TABLE_ID_ROWS) : []
  const ids = nav.map((n) => (n && n.id != null ? String(n.id).slice(0, 40) : ''))
  if (!ids.some(Boolean)) return ''
  return ' · 행 ID: ' + ids.map((id, i) => `${i + 1}) ${id || '-'}`).join(', ')
}

/**
 * 구조화 블록을 대화 이력용 경량 텍스트로 직렬화한다(AA-46).
 * @param {object} block - { type, data }
 * @returns {string}
 */
function blockToText(block) {
  if (!block || !block.type) return ''
  const d = block.data || {}
  switch (block.type) {
    case 'text': return d.markdown || ''
    case 'table': return '[표] ' + (d.caption || `${(d.columns || []).length}열`) + tableRowIds(d)
    case 'chart': return '[차트] ' + (d.title || d.chartType || '')
    case 'card': return '[카드] ' + (d.title || '')
    case 'link': return '[링크] ' + (d.label || d.url || '')
    // 후보 선택(refine) 시 LLM이 기존 입력을 유지한 채 재호출할 수 있도록 preview 요약을 포함
    case 'draft_confirm':
      return ('[접수 등록 초안] ' + Object.entries(d.preview || {})
        .map(([k, v]) => k + ': ' + v).join(', ')).slice(0, 400)
    // 상담 등록 초안(AA-117) — 접수 초안과 동일 원칙으로 preview 요약을 직렬화(누락 시 후속
    // 질의에서 카드 내용이 조용히 유실된다)
    case 'consultation_draft':
      return ('[상담 등록 초안] ' + Object.entries(d.preview || {})
        .map(([k, v]) => k + ': ' + v).join(', ')).slice(0, 400)
    // 관리 설정 초안(AA-119) — preview 는 배열([{label,value}])이라 위 두 case의
    // Object.entries 패턴을 그대로 쓰면 "0: [object Object]"가 된다. 전용 직렬화 필수.
    case 'admin_draft':
      return ('[관리 설정 초안] ' + (Array.isArray(d.preview) ? d.preview : [])
        .map((p) => p.label + ': ' + p.value).join(', ')).slice(0, 400)
    // 초안 본문은 LLM 생성물이라 도구 재조회로 복원 불가 — 후속 수정 요청("두 번째 문장만 고쳐줘")
    // 컨텍스트 유지를 위해 본문을 절단 포함해 직렬화한다 (리뷰 F2 반영)
    case 'reply_draft':
      return '[응대 초안] ' + (d.title ? d.title + '\n' : '') + String(d.draft || '').slice(0, 600)
    default: return ''
  }
}

export const useAssistantStore = defineStore('assistant', {
  state: () => ({
    /** 패널 열림 여부 */
    open: false,
    /** 입력창 임시 텍스트 */
    draft: '',
    /** 전송/스트리밍 진행 중 여부 */
    sending: false,
    /** 최초 인사 메시지 노출 여부 */
    greeted: false,
    /** 프로액티브 브리핑 1회 호출 여부 (greeted와 동일 시맨틱 — 세션 내 1회) */
    briefed: false,
    /**
     * 메시지 목록 — { _uid, kind:'user'|'bot'|'error'|'block', text?, block?:{type,data}, committing?, done? }
     * 초안 카드는 recvAt(수신 시각)과 복원 안내 restoreNote('expired'|'commitUnknown')를 더 가질 수 있다(AA-143)
     */
    messages: [],
    /** 음성 입력 동의 여부 (state만 — UI/로직은 이후 주차) */
    micOptedIn: false,
    /** 진행 중 HITL 접수 confirmation_token (선택) */
    draftToken: null,
    /** 활성 고객사 ID — 초기값은 로그인 사용자의 partnerId */
    activePartnerId: store.state.auth.user?.id?.partnerId || '',
    /**
     * 세션 세대 카운터 — reset()마다 증가. 비동기 응답(브리핑/commit)이 reset 이전에
     * 시작됐다면 도착 시점에 세대가 달라져 있어, 이전 계정의 응답이 새 계정 대화에
     * push되는 것을 막는 가드로 쓰인다.
     */
    sessionEpoch: 0,
    /**
     * 진행 중 쓰기 작업(AA-143) — 'repair'|'consultation'|'admin'|null. 매 전송에 state.task 로 보내고
     * 초안 카드 수신·[등록]·[취소]와 ai-service 가 done 이벤트로 돌려준 값으로 갱신한다. 서버가 도구 노출에만 쓰는 값이다.
     */
    convTask: null
  }),
  getters: {
    /**
     * ai-service로 전달할 대화 이력 (user/bot/block을 role 매핑).
     * block 메시지는 후속 질의 컨텍스트 유지를 위해 경량 텍스트로 직렬화한다(AA-46).
     * @returns {{role:string, content:string}[]}
     */
    chatHistory: (state) => state.messages
      .filter((m) => m.kind === 'user' || m.kind === 'bot' || m.kind === 'block')
      .map((m) => {
        if (m.kind === 'user') return { role: 'user', content: m.text }
        if (m.kind === 'bot') return { role: 'assistant', content: m.text }
        return { role: 'assistant', content: blockToText(m.block) }
      })
  },
  actions: {
    /** 패널 토글 */
    toggle() {
      this.open = !this.open
    },
    /**
     * 메시지를 추가하고 배열의 reactive proxy 요소를 반환한다.
     * ⚠️ SSE 토큰 누적은 raw 객체가 아닌 이 반환값(proxy)을 참조해야 반응성이 트리거된다.
     * @param {object} m - 메시지 객체
     * @returns {object} messages 배열에 push된 reactive proxy 요소
     */
    pushMessage(m) {
      m._uid = ++uidSeq
      this.messages.push(m)
      return this.messages[this.messages.length - 1]
    },
    /**
     * 활성 고객사 ID를 설정한다. 동일값이면 스킵하여 bridge 루프/불필요 갱신을 막는다.
     * @param {string} id - partnerId (빈 문자열 = 전체)
     */
    setActivePartnerId(id) {
      if (this.activePartnerId !== id) this.activePartnerId = id
    },
    /** 대화 스냅샷을 sessionStorage 에 저장한다(AA-143 새로고침 복원). 로그인 토큰이 없으면 건너뛴다. */
    persist() {
      saveConversation({ greeted: this.greeted, briefed: this.briefed, task: this.convTask, messages: this.messages })
    },
    /**
     * 초안 카드 수신 — 작업을 카드 kind 로 올린다. done 이 오지 않는 턴(LLMError·중지)에도 다음 턴이 이어진다.
     * @param {string|null} kind
     */
    startTask(kind) {
      if (kind) this.convTask = kind
    },
    /**
     * 카드 [등록]·[취소]로 작업을 비운다. 카드 kind 가 진행 작업과 같을 때만 — 다른 흐름 중
     * 예전 카드를 눌러도 진행 작업이 지워지지 않는다. 같은 kind 의 열린 카드(!done·!committing)가 남아 있으면
     * 그 카드로 작업이 이어지므로 비우지 않는다(새 카드가 떠 있는데 옛 카드 [등록]).
     * @param {string|null} kind
     * @returns {boolean} 비웠는가
     */
    clearTask(kind) {
      if (!kind || this.convTask !== kind) return false
      if (this.messages.some((m) => !m.done && !m.committing && taskOfBlock(m.block) === kind)) return false
      this.convTask = null
      return true
    },
    /**
     * done 이벤트의 서버 상태를 반영한다. 서버 task 가 전송 때 값과 같으면 서버는 '유지'한 것이므로
     * 요청 중 로컬에서 바뀐 값(카드 수신·[등록]·[취소])을 우선하고, 다르면 서버 판정(시작·교체·말로 한 취소)을 따른다.
     * 단 요청 중 [등록]·[취소]로 비운 kind(ctx.cleared)와 같은 서버값은 무시한다 — 서버는 그 비움을 모른 채
     * 이번 턴 카드 kind 를 돌려준다.
     * @param {object} raw - done data 의 state 객체
     * @param {{epoch:number, task:string|null, cleared?:string[]}} ctx - 전송 시점 스냅샷
     */
    applyServerState(raw, ctx) {
      if (ctx.epoch !== this.sessionEpoch) return // reset(계정 전환) 이후 늦게 온 응답
      const next = sanitizeTask(raw && raw.task)
      if (next === ctx.task || (next && ctx.cleared && ctx.cleared.includes(next))) return
      this.convTask = next
    },
    /**
     * 대화 전체 초기화 — 로그아웃·로그인 시 호출하여 이전 계정의 채팅 이력이
     * 다음 로그인 계정에 노출되지 않도록 한다. 진행 작업과 sessionStorage 저장분(AA-143)도 지운다.
     */
    reset() {
      this.sessionEpoch++
      this.open = false
      this.sending = false
      this.messages = []
      this.greeted = false
      this.briefed = false
      this.draft = ''
      this.draftToken = null
      // 이전 계정의 commit 타겟이 남지 않도록 현재 로그인 사용자 기준으로 재계산
      this.activePartnerId = store.state.auth.user?.id?.partnerId || ''
      this.convTask = null
      clearConversation()
    }
  }
})
